import { useNavigate } from 'react-router-dom'
import { formatPhoneNumber } from 'react-phone-number-input'
import moment from 'moment'
import { useCallback } from 'react'
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro'

import {
  getCurrentUser,
  getFinancialProfile,
  selectIsCurrentUserScorp,
} from '@/selectors/user.selectors'
import { UploadDocumentType } from '@/constants/businessConstants'
import { getLegalEntityName } from '@/constants/onboardingConstants'
import { UserDocumentCategoryIdentifier } from '@/featuresV1/Admin/UserDocumentCategories/userDocumentCategory.constants'
import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { formatEin } from '@/componentsV1/Finances/shared/utils'
import { fetchUserDocuments } from '@/features/user/your-practice/documents/lib/userDocuments.slice'

import { Text, Icon } from '@/ui'
import { TypographyStyles } from '@/styles'
import { PracticeProfileEntry } from '../PracticeProfileEntry'
import { Form2553UploadDialog } from './Form2553UploadDialog'
import { CP261LetterUploadDialog } from './CP261LetterUploadDialog'
import { IncorporationDocumentUploadDialog } from './IncorporationDocumentUploadDialog'
import { EINLetterUploadDialog } from './EINLetterUploadDialog'
import { StateRegistrationUploadDialog } from './StateRegistrationUploadDialog'
import {
  CP261Tooltip,
  EinLetterTooltip,
  Form2553Tooltip,
  IncorporationDocTooltip,
  StateRegistrationTooltip,
} from '../PracticeProfileTooltips'

/**
 * The form for the business profile.
 */
export const YourBusiness = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isScorp = useReselector(selectIsCurrentUserScorp)
  const user = useReselector(getCurrentUser)
  const fp = useReselector(getFinancialProfile)

  const refetchDocuments = useCallback(() => {
    dispatch(fetchUserDocuments(false, true))
  }, [dispatch])

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex flex-row gap-2 items-center">
          <Icon
            icon={solid('suitcase')}
            backgroundStyle="rounded"
            backgroundColor="emphasis"
            size="sm"
          />
          <Text variant={TypographyStyles.LABEL_MD}>Your Business</Text>
        </div>
        <Icon
          icon={regular('pen-to-square')}
          onClick={() => navigate('/practice/profile/editBusiness')}
        />
      </div>

      <PracticeProfileEntry label="Business Name" value={fp?.businessName} />
      <PracticeProfileEntry
        label="Business Entity"
        value={getLegalEntityName(fp?.businessEntity)}
      />
      <PracticeProfileEntry
        label="Business EIN Number"
        value={formatEin(fp?.einNumber)}
      />
      <PracticeProfileEntry
        label="Year Practice Started"
        value={fp?.yearPracticeStarted}
      />
      <div className="h-[24px]" />
      <PracticeProfileEntry
        label="Business Address"
        value={fp?.businessAddress}
      />
      <PracticeProfileEntry label="State of Residence" value={fp?.homeState} />
      {isScorp && (
        <>
          <PracticeProfileEntry
            label="Business Registration State"
            value={fp?.scorpRegistrationState}
          />
          <PracticeProfileEntry
            label="S Corp Election Date"
            value={
              fp?.entityChangeElectionDate &&
              moment
                .utc(fp.entityChangeElectionDate)
                .format(DATE_FORMATS.DISPLAY_LONG)
            }
          />
        </>
      )}
      <PracticeProfileEntry
        label="Business Phone"
        value={user?.phoneNumber && formatPhoneNumber(user?.phoneNumber)}
      />
      <PracticeProfileEntry label="Business Email" value={user?.email} />

      {isScorp ? (
        <>
          <Form2553UploadDialog
            label="Form 2553"
            popupContent={<Form2553Tooltip />}
            documentCat={UserDocumentCategoryIdentifier.form2553}
            onUploadSuccess={refetchDocuments}
          />

          <CP261LetterUploadDialog
            label="CP261 Letter"
            popupContent={<CP261Tooltip />}
            documentCat={UserDocumentCategoryIdentifier.cp261Notice}
            onUploadSuccess={refetchDocuments}
          />

          <IncorporationDocumentUploadDialog
            label="Business Incorporation Documents"
            popupContent={<IncorporationDocTooltip />}
            documentCat={UserDocumentCategoryIdentifier.businessIncorporation}
            documentType={UploadDocumentType.INCORPORATION}
            onUploadSuccess={refetchDocuments}
          />
        </>
      ) : (
        <>
          <EINLetterUploadDialog
            label="EIN Letter"
            popupContent={<EinLetterTooltip />}
            documentCat={UserDocumentCategoryIdentifier.einDocuments}
            additionalTrackingProperties={{
              document_category: 'ein_letter',
              business_entity: fp?.businessEntity,
            }}
            onUploadSuccess={refetchDocuments}
          />
          <StateRegistrationUploadDialog
            label="State Registration"
            popupContent={<StateRegistrationTooltip />}
            documentCat={UserDocumentCategoryIdentifier.stateRegistration}
            documentType={UploadDocumentType.INCORPORATION}
            additionalTrackingProperties={{
              document_category: 'state_registration',
              business_entity: fp?.businessEntity,
            }}
            onUploadSuccess={refetchDocuments}
          />
        </>
      )}
    </div>
  )
}
